import type { JSX } from '@builder.io/qwik';
import { component$, useContext } from '@builder.io/qwik';
import { cva } from 'class-variance-authority';
import type { LinkValueInterface } from '~/types/AppData/Layout/LinkValueInterface';
import { NavContext } from '~/contexts/NavContext';
import { getCSSFromTheme } from '~/utilities/getCSSFromTheme';
import { AppDataContext } from '~/contexts/AppDataContext';
import { getPathFromUrl } from '~/utilities/getPathFromUrl';

const navItemLink = cva('w-full h-full flex items-center box-border', {
  variants: {
    level: {
      0: '',
      1: 'text-wrap',
    },
    underline: {
      true: 'hover:underline',
      false: '',
    },
  },
});

const navWrap = cva('list-none flex align-center group', {
  variants: {
    level: {
      0: 'h-full',
      1: 'min-w-[125px]',
      2: 'min-w-max',
    },
  },
});

type NavItemLinkType = {
  item: LinkValueInterface;
  level: number;
};

export default component$(({ item, level }: NavItemLinkType): JSX.Element => {
  const {
    payloadToSSR: { mainCategory, pagePath },
  } = useContext(AppDataContext);
  const { theme } = useContext(NavContext);
  const mainCategoryPath = `/${mainCategory?.slug}`;
  const isActiveCategory =
    mainCategoryPath === getPathFromUrl(item.link.url) &&
    pagePath.startsWith(mainCategoryPath);

  const themeMapMain = {
    color: theme.LinkColorTypeNormal.value,
    hoverColor: theme.LinkColorTypeHover.value,
    sizeSm: theme.LinkFontSizeSmall.value,
    sizeMd: theme.LinkFontSizeMedium.value,
    sizeLg: theme.LinkFontSizeLarge.value,
    border: theme.LinkHoverBorderBottom?.value,
    borderBottomHover: theme.LinkHoverBorderBottomWidth?.value,
    borderBottom: isActiveCategory
      ? theme.LinkActiveBorderBottomWidth?.value
      : 0,
    paddingTopHover: theme.LinkHoverBorderBottomWidth?.value,
    paddingTop: isActiveCategory ? theme.LinkActiveBorderBottomWidth?.value : 0,
  };

  const themeMapSub = {
    color: theme.LinkColorTypeNormal.value,
    hoverColor: theme.LinkColorTypeHover.value,
    sizeSm: theme.SubMenuLinksFontSize.value,
  };

  // Link text classes are inherited from the parent for subnavs, but needed for top level links
  // We do this to minimize the classes that are added to each link element
  const linkClass =
    level === 0
      ? getCSSFromTheme(
          themeMapMain,
          navItemLink({ level: 0, underline: theme.LinkHoverUnderline?.value })
        )
      : getCSSFromTheme(
          themeMapSub,
          navItemLink({ level: 1, underline: theme.LinkHoverUnderline?.value })
        );

  return (
    <li class={navWrap({ level: level > 2 ? 2 : (level as 0 | 1 | 2) })}>
      <span class='flex items-center cursor-pointer w-full'>
        <a href={item.link.url} class={linkClass}>
          {item.link.text}
        </a>
      </span>
    </li>
  );
});
